import { WarningFilled } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Alerts } from '@client/components/_ui/display/Alerts';
import { ComponentPreview } from '@client/components/_ui/display/ComponentPreview';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { User } from '@client/components/_ui/display/User';
import { Space } from '@client/components/_ui/layout/Space';
import { buildInitialWhereFromFilters, FilterBar } from '@client/components/filters';
import { INCIDENT_FILTER_STALE_TIME_MS } from '@client/components/incidents/constants';
import { useIncidentFilterConfig } from '@client/components/incidents/useIncidentFilterConfig';
import { TableViewsBar, useEntityBuiltInViews, useTableViews } from '@client/components/table-views';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { selectOrg, selectUser, useAuthStore } from '@client/stores/AuthStore';
import { queryClient, type RouterInputs, type RouterOutputs, trpcOptions } from '@client/trpc/client';
import { LIST_ROUTE_PREFETCH_OPTIONS, LIST_ROUTE_QUERY_OPTIONS } from '@client/trpc/query-options';
import type { ShareableRouteContext } from '@client/types/router';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, ORG_ROLES } from '@shared/constants';
import { FilterOperatorSchema } from '@shared/schemas/filter';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Tag, Tooltip } from 'antd';
import { useState } from 'react';
import { z } from 'zod';
import {
    runDetailRoutePrefetchWhenAuthorised,
    shouldPrefetchDetailRouteData,
    yieldToPrefetchBatch,
} from '../../detail-route-prefetch';
import {
    freshIncidentsListInput,
    freshIncidentsListInputWithWhere,
    LIST_TABLE_PERSIST_KEYS,
} from '../../fresh-list-inputs';
import { runListRoutePrefetch } from '../../list-route-prefetch';

const IncidentSearchSchema = z.object({
    'incident-filters': z
        .array(
            z.union([
                z.object({
                    id: z.string(),
                    filterId: z.literal('owner-id'),
                    operator: FilterOperatorSchema,
                    value: z.union([z.array(z.coerce.bigint()), z.coerce.bigint()]),
                }),
                z.object({
                    id: z.string(),
                    filterId: z.string(),
                    operator: FilterOperatorSchema,
                    value: z.any(),
                }),
            ]),
        )
        .optional()
        .catch(undefined),
    view: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/compliance/incidents/')({
    staticData: {
        ui: {
            title: 'Incidents',
            subtitle: 'Create, track, and manage compliance incidents and their resolution status',
            widthVariant: 'fullWidth',
            icon: ICONS.incident,
        },
    },
    validateSearch: zodValidator(IncidentSearchSchema),
    beforeLoad: ({
        context,
        params,
        search,
        location,
        preload,
        abortController,
        cause,
    }): ShareableRouteContext | Promise<ShareableRouteContext> => {
        // Warm the saved-views round trip and (when the input is exactly computable)
        // the list query itself while the auth gate renders — see the claims list
        // route and `list-route-prefetch.ts` (CUR-1036, task 2.3). The filter columns
        // ride the same batch.
        runDetailRoutePrefetchWhenAuthorised(
            () => {
                const authState = useAuthStore.getState();
                const user = selectUser(authState);
                return shouldPrefetchDetailRouteData({
                    hasValidToken: authState.hasValidToken({
                        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
                    }),
                    userRoles: user?.roles,
                    allowedRoles: [ORG_ROLES.USER, ORG_ROLES.SHARED_EDIT_USER, ORG_ROLES.ORG_ADMIN],
                    hydratedOrg: selectOrg(authState),
                    routeOrgId: params.orgId,
                    requiredModule: 'compliance',
                });
            },
            () => {
                runListRoutePrefetch<RouterInputs['incident']['listIncidents'], 'INCIDENT'>({
                    savedViewsUtils: context.trpcUtils.savedView.listSavedViews,
                    entityType: 'INCIDENT',
                    pathname: location.pathname,
                    urlFilterCount: search['incident-filters']?.length ?? 0,
                    urlViewId: search.view,
                    defaultInput: freshIncidentsListInput(),
                    prefetchList: (input) => {
                        void context.trpcUtils.incident.listIncidents.prefetch(input, LIST_ROUTE_PREFETCH_OPTIONS);
                    },
                });
                // The filter bar's options come from this; warm it with the list so it is not a second batch after mount.
                void queryClient.prefetchQuery(
                    trpcOptions.incident.getIncidentColumnsForFiltering.queryOptions(undefined, {
                        staleTime: INCIDENT_FILTER_STALE_TIME_MS,
                        meta: LIST_ROUTE_PREFETCH_OPTIONS.meta,
                    }),
                );
            },
            { defer: !preload, signal: abortController.signal },
        );
        return cause === 'enter' ? yieldToPrefetchBatch() : undefined;
    },
    component: IncidentSearch,
});

type QueryParams = RouterInputs['incident']['listIncidents'];
type IncidentData = RouterOutputs['incident']['listIncidents'];
type IncidentRow = NonNullable<IncidentData>['rows'][number];
type IncidentOrderBy = NonNullable<QueryParams['orderBy']>;

function IncidentSearch() {
    const orgId = useOrgId();
    const enableAiIntake = useFeatureFlag('enable-ai-intake', false);
    const { trpc } = useTrpc();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();
    const searchParams = Route.useSearch();

    const { config: filterConfig, ready: filterConfigReady } = useIncidentFilterConfig();

    const [input, setInput] = usePathState<QueryParams>(
        'query-params',
        freshIncidentsListInputWithWhere(buildInitialWhereFromFilters(filterConfig, searchParams['incident-filters'])),
    );

    // Postpone the list query until saved views resolve which view loads first, so we fetch once
    // with the resolved where/sort/pageSize instead of unfiltered-then-refiltered (set by onResolved).
    const [viewsReady, setViewsReady] = useState(false);

    const { data, isPending, refetch } = trpc.incident.listIncidents.useQuery(input, {
        enabled: filterConfigReady && viewsReady,
        ...LIST_ROUTE_QUERY_OPTIONS,
    });
    const columnHelper = createColumnHelper<IncidentRow, IncidentOrderBy>();

    const columns = [
        columnHelper.display({
            id: 'indicator',
            header: '',
            cell: ({ row }) => (
                <Alerts id={row.id} entity="incident" indicatorColor={row.indicatorColor} key={row.id} />
            ),
            width: 40,
            fixed: 'left',
            align: 'center',
            hideable: false,
        }),
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ row }) => (
                <>
                    {row.isBreachWithoutObligations && (
                        <Tooltip
                            title={
                                <>
                                    This incident is a breach and requires at least one obligation to be selected. You
                                    can view the incident details and select obligations when ready.
                                </>
                            }
                        >
                            (Out of sync){' '}
                        </Tooltip>
                    )}
                    {String(row.id)}
                </>
            ),
            fixed: 'left',
            hideable: false,
        }),
        columnHelper.accessor('productImpacted', {
            id: 'productImpacted',
            header: 'Business area',
            sortable: 'productImpacted',
        }),
        columnHelper.display({
            id: 'type',
            header: 'Type',
            cell: ({ row }) =>
                row.IncidentType && (
                    // Outlined in both cases: the solid error tag put white text on #ff4d4f (3.3:1).
                    // Breach types keep their emphasis through the error hue and weight.
                    <Tag
                        variant="outlined"
                        color={row.IncidentType.isBreach ? 'red' : 'default'}
                        className={row.IncidentType.isBreach ? 'font-semibold' : undefined}
                    >
                        {row.IncidentType?.label}
                    </Tag>
                ),
        }),
        columnHelper.display({
            id: 'description',
            header: 'Incident description',
            cell: ({ row }) => <ComponentPreview>{row.description}</ComponentPreview>,
            className: 'max-w-xs align-top',
        }),
        columnHelper.accessor('dtIdentified', {
            id: 'dtIdentified',
            header: 'Date identified',
            sortable: 'dtIdentified',
            cell: ({ row }) => formatDate(row.dtIdentified),
        }),
        columnHelper.accessor('dtClosed', {
            id: 'dtClosed',
            header: 'Date closed',
            sortable: 'dtClosed',
            cell: ({ value }) => formatDate(value),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            sortable: 'status',
            header: 'Status',
            cell: ({ row }) => row.status && <StatusBadge status={row.status} module="incident" />,
        }),
        columnHelper.display({
            id: 'aiBreaches',
            header: 'AI breaches',
            cell: ({ row }) =>
                row.unresolvedAiBreachCount > 0 ? (
                    <Tooltip
                        title={`${row.unresolvedAiBreachCount} unresolved AI-detected breach${row.unresolvedAiBreachCount > 1 ? 'es' : ''}`}
                    >
                        <span className="flex items-center gap-1 text-orange-500">
                            <WarningFilled />
                            <span>{row.unresolvedAiBreachCount}</span>
                        </span>
                    </Tooltip>
                ) : null,
            width: 100,
        }),
        columnHelper.display({
            id: 'owner',
            header: 'Owner',
            cell: ({ row }) => (
                <User userId={row.Owner.userId} photoUrl={row.Owner.photoUrl} name={row.Owner.fullName} />
            ),
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.OpenInNewTab
                    preload={false}
                    to="/console/$orgId/compliance/incidents/details/$id/manage"
                    params={{ orgId, id: row.id }}
                    tooltip="Open in new tab"
                />
            ),
            width: 50,
        }),
    ];

    const tableProps = usePrismaTable<IncidentRow, QueryParams['where'], IncidentOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !viewsReady || isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        emptyState: { noRecordsTitle: 'No incidents yet' },
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            persistKey: LIST_TABLE_PERSIST_KEYS.incidents,
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    const builtInViews = useEntityBuiltInViews('owner-id');
    const views = useTableViews<NonNullable<QueryParams['where']>, IncidentOrderBy>({
        entityType: 'INCIDENT',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'incident-search-filters',
        builtInViews,
        initialFilters: searchParams['incident-filters'],
        initialActiveViewId: searchParams.view,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        columnPersistKey: 'incident-search-columns',
        defaultOrderBy: { id: 'desc' },
        defaultPageSize: DEFAULT_PAGE_SIZE,
        onResolved: () => setViewsReady(true),
    });

    return (
        <PageController route={Route}>
            <TableViewsBar controller={views.controller} />
            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                actions={
                    <Space size="small">
                        {enableAiIntake && (
                            <Button.Link
                                to="/console/$orgId/compliance/intake"
                                params={{ orgId }}
                                variant="secondary"
                                beta
                            >
                                Smart intake
                            </Button.Link>
                        )}
                        <Button.Add.Link
                            to="/console/$orgId/compliance/incidents/add"
                            params={{ orgId }}
                            variant="primary"
                            label="New incident"
                        />
                    </Space>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/compliance/incidents/details/$id/manage',
                                params: { orgId, id },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
